import React, { useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { useAuth } from "../services/use-auth";
import { ensureSupabaseRoleClaim } from "../services/supabaseClaim";
import { doc, setDoc } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import * as ROUTES from "../constants/routes";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "../components/Button";
import {
  Typography,
  FormControlLabel,
  Checkbox,
  Link,
} from "@mui/material";

import AuthLayout from "./AuthLayout";
import { FormCard, AuthTitle, TextLink, ErrorText } from "./authStyles";
import PasswordRequirementsChecklist from "./PasswordRequirementsChecklist";
import { evaluatePasswordRequirements } from "./passwordRequirements";

const SignUpComponent = () => {
  const authHook = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    if (authHook?.auth?.currentUser) {
      navigate(ROUTES.LANDING);
    }
  }, [authHook?.auth?.currentUser, navigate]);

  // Fetch TOS PDF URL from Firebase Storage
  useEffect(() => {
    const fetchTosUrl = async () => {
      if (authHook.storage) {
        try {
          const tosRef = ref(authHook.storage, "legal/terms-of-service.pdf");
          const url = await getDownloadURL(tosRef);
          setTosUrl(url);
        } catch (error) {
          console.warn("TOS PDF not found in storage:", error);
          setTosUrl("#");
        }
      }
    };

    fetchTosUrl();
  }, [authHook.storage]);

  const [signUpData, setSignUpData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    passwordConfirm: "",
  });
  const [entryErrors, setEntryErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    passwordConfirm: false,
  });
  const [entryFeedback, setEntryFeedback] = useState({
    firstName: null,
    lastName: null,
    email: null,
    password: null,
    passwordConfirm: null,
  });
  const [errorFeedback, setErrorFeedback] = useState(null);
  const [tosAgreed, setTosAgreed] = useState(false);
  const [tosUrl, setTosUrl] = useState(null);
  const [processing, setProcessing] = useState(false);

  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasNumber: false,
    hasSymbol: false,
    passwordsMatch: false,
  });

  const handleChange = (event) => {
    setSignUpData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));

    if (
      event.target.name === "password" ||
      event.target.name === "passwordConfirm"
    ) {
      updatePasswordRequirements({
        ...signUpData,
        [event.target.name]: event.target.value,
      });
    }

    if (entryErrors.firstName) validateFirstName();
    if (entryErrors.lastName) validateLastName();
    if (entryErrors.email) validateEmail();
    if (entryErrors.password) validatePassword();
    if (entryErrors.passwordConfirm) validatePasswordConfirm();
  };

  const updatePasswordRequirements = (data) => {
    setPasswordRequirements(
      evaluatePasswordRequirements(data.password, data.passwordConfirm),
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !invalid) {
      event.preventDefault();
      submitSignUp();
    }
  };

  const validateFirstName = () => {
    if (signUpData.firstName === "") {
      setEntryErrors((prev) => ({ ...prev, firstName: true }));
      setEntryFeedback((prev) => ({
        ...prev,
        firstName: "You must enter a First Name",
      }));
    } else {
      const containsTwo = signUpData.firstName.includes(" ");
      if (containsTwo) {
        setEntryErrors((prev) => ({ ...prev, firstName: true }));
        setEntryFeedback((prev) => ({
          ...prev,
          firstName: "You must enter only one name with no spaces",
        }));
      } else {
        setEntryErrors((prev) => ({ ...prev, firstName: false }));
        setEntryFeedback((prev) => ({ ...prev, firstName: null }));
      }
    }
  };

  const validateLastName = () => {
    if (signUpData.lastName === "") {
      setEntryErrors((prev) => ({ ...prev, lastName: true }));
      setEntryFeedback((prev) => ({
        ...prev,
        lastName: "You must enter a Last Name",
      }));
    } else {
      const containsTwo = signUpData.lastName.includes(" ");
      if (containsTwo) {
        setEntryErrors((prev) => ({ ...prev, lastName: true }));
        setEntryFeedback((prev) => ({
          ...prev,
          lastName: "Please hyphenate multiple last names",
        }));
      } else {
        setEntryErrors((prev) => ({ ...prev, lastName: false }));
        setEntryFeedback((prev) => ({ ...prev, lastName: null }));
      }
    }
  };

  const validateEmail = () => {
    if (signUpData.email === "") {
      setEntryErrors((prev) => ({ ...prev, email: true }));
      setEntryFeedback((prev) => ({
        ...prev,
        email: "You must enter a email",
      }));
    } else {
      const containsAtAndPeriod =
        signUpData.email.includes("@") && signUpData.email.includes(".");
      if (!containsAtAndPeriod) {
        setEntryErrors((prev) => ({ ...prev, email: true }));
        setEntryFeedback((prev) => ({
          ...prev,
          email: "You must enter a valid email address",
        }));
      } else {
        setEntryErrors((prev) => ({ ...prev, email: false }));
        setEntryFeedback((prev) => ({ ...prev, email: null }));
      }
    }
  };

  const validatePassword = () => {
    if (signUpData.password === "") {
      setEntryErrors((prev) => ({ ...prev, password: true }));
      setEntryFeedback((prev) => ({
        ...prev,
        password: "You must enter a password",
      }));
    } else {
      const meetsRequirements =
        passwordRequirements.minLength &&
        passwordRequirements.hasNumber &&
        passwordRequirements.hasSymbol;
      if (!meetsRequirements) {
        setEntryErrors((prev) => ({ ...prev, password: true }));
        setEntryFeedback((prev) => ({
          ...prev,
          password: "Password must meet all requirements below",
        }));
      } else {
        setEntryErrors((prev) => ({ ...prev, password: false }));
        setEntryFeedback((prev) => ({ ...prev, password: null }));
      }
    }
  };

  const validatePasswordConfirm = () => {
    if (signUpData.passwordConfirm === "") {
      setEntryErrors((prev) => ({ ...prev, passwordConfirm: true }));
      setEntryFeedback((prev) => ({
        ...prev,
        passwordConfirm: "You must confirm your password",
      }));
    } else {
      const match = signUpData.password === signUpData.passwordConfirm;
      if (!match) {
        setEntryErrors((prev) => ({ ...prev, passwordConfirm: true }));
        setEntryFeedback((prev) => ({
          ...prev,
          passwordConfirm: "Your passwords do not match",
        }));
      } else {
        setEntryErrors((prev) => ({ ...prev, passwordConfirm: false }));
        setEntryFeedback((prev) => ({ ...prev, passwordConfirm: null }));
      }
    }
  };

  const invalid =
    signUpData.email === "" ||
    signUpData.password === "" ||
    signUpData.passwordConfirm === "" ||
    signUpData.firstName === "" ||
    signUpData.lastName === "" ||
    entryErrors.email ||
    entryErrors.password ||
    entryErrors.passwordConfirm ||
    entryErrors.firstName ||
    entryErrors.lastName ||
    !passwordRequirements.minLength ||
    !passwordRequirements.hasNumber ||
    !passwordRequirements.hasSymbol ||
    !passwordRequirements.passwordsMatch ||
    !tosAgreed;

  const submitSignUp = async () => {
    setProcessing(true);
    const purifyStr = (str) => {
      const lower = str.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    };
    const firstName = purifyStr(signUpData.firstName);
    const lastName = purifyStr(signUpData.lastName);
    const username = firstName.concat(" ").concat(lastName);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        authHook.auth,
        signUpData.email,
        signUpData.password,
      );
      const user = userCredential.user;
      await setDoc(
        doc(authHook.db, "users", user.uid),
        {
          email: user.email,
          username: username,
          firstName: signUpData.firstName,
          lastName: signUpData.lastName,
          onboarded: false,
          completedOnboardingTour: false,
          roles: {
            admin: false,
            superAdmin: false,
            bookkeeper: false,
            auditor: false,
            subscriber: false,
          },
        },
        { merge: true },
      );

      // N1 (Option B): the mintSupabaseRoleClaim trigger runs asynchronously
      // after Firebase creates the user, so this brand-new session's FIRST ID
      // token does not carry `role: "authenticated"` yet - the vendor's own
      // documented caveat. Force refreshes until it does. Best-effort: the claim
      // is inert in this app, so a failure here must never block a sign-up.
      try {
        await ensureSupabaseRoleClaim(user, { retries: 3 });
      } catch {
        // Swallowed deliberately - see above. The next page load retries.
      }

      navigate(ROUTES.LANDING);
    } catch (error) {
      const errorCode = error.code;
      console.error(errorCode, error.message);

      let feedbackMessage;

      switch (errorCode) {
        case "auth/invalid-email":
          feedbackMessage = "Invalid email format.";
          break;
        case "auth/user-disabled":
          feedbackMessage = "User account is disabled.";
          break;
        case "auth/user-not-found":
          feedbackMessage = "Email not found.";
          break;
        case "auth/wrong-password":
          feedbackMessage = "Incorrect password.";
          break;
        case "auth/email-already-in-use":
          feedbackMessage = "This email is already in use.";
          break;
        case "auth/weak-password":
          feedbackMessage = "Weak password.";
          break;
        case "auth/account-exists-with-different-credential":
          feedbackMessage = "Account exists with different credentials.";
          break;
        case "auth/invalid-credential":
          feedbackMessage = "Invalid credentials.";
          break;
        case "auth/operation-not-allowed":
          feedbackMessage = "Operation not allowed.";
          break;
        case "auth/network-request-failed":
          feedbackMessage = "Network error occurred.";
          break;
        case "auth/too-many-requests":
          feedbackMessage = "Too many requests. Slow down!";
          break;
        case "auth/requires-recent-login":
          feedbackMessage = "Recent login required.";
          break;
        default:
          feedbackMessage = "An unknown error occurred. Check console.";
      }
      setErrorFeedback(feedbackMessage);
    } finally {
      setProcessing(false);
    }
  };

  const fieldColor = theme.palette.mode === "light" ? "secondary" : "primary";

  return (
    <AuthLayout>
      <FormCard>
        <AuthTitle theme={theme}>Sign Up</AuthTitle>
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pb: 3,
            width: "100%",
          }}
          noValidate
          autoComplete="off"
          onKeyDown={handleKeyDown}>
          {/* First Name + Last Name — side by side */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}>
            <TextField
              sx={{ flex: 1 }}
              label="First Name"
              name="firstName"
              variant="outlined"
              color={fieldColor}
              value={signUpData.firstName}
              onChange={handleChange}
              onBlur={validateFirstName}
              error={entryErrors.firstName}
              helperText={entryFeedback.firstName}
              required
            />
            <TextField
              sx={{ flex: 1 }}
              label="Last Name"
              name="lastName"
              variant="outlined"
              color={fieldColor}
              value={signUpData.lastName}
              onChange={handleChange}
              onBlur={validateLastName}
              error={entryErrors.lastName}
              helperText={entryFeedback.lastName}
              required
            />
          </Box>

          {/* Email — full width */}
          <TextField
            fullWidth
            label="Email Address"
            name="email"
            variant="outlined"
            color={fieldColor}
            value={signUpData.email}
            onChange={handleChange}
            onBlur={validateEmail}
            error={entryErrors.email}
            helperText={entryFeedback.email}
            required
          />

          {/* Password + Confirm Password — side by side */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}>
            <TextField
              sx={{ flex: 1 }}
              label="Password"
              type="password"
              name="password"
              variant="outlined"
              color={fieldColor}
              value={signUpData.password}
              onChange={handleChange}
              onBlur={validatePassword}
              error={entryErrors.password}
              helperText={entryFeedback.password}
              required
            />
            <TextField
              sx={{ flex: 1 }}
              label="Confirm Password"
              name="passwordConfirm"
              type="password"
              variant="outlined"
              color={fieldColor}
              value={signUpData.passwordConfirm}
              onChange={handleChange}
              onBlur={validatePasswordConfirm}
              error={entryErrors.passwordConfirm}
              helperText={entryFeedback.passwordConfirm}
              required
            />
          </Box>

          {/* Password Requirements Checklist — below password row */}
          {(signUpData.password.length > 0 ||
            signUpData.passwordConfirm.length > 0) && (
            <PasswordRequirementsChecklist requirements={passwordRequirements} />
          )}

          {/* Terms of Service Agreement */}
          <FormControlLabel
            control={
              <Checkbox
                checked={tosAgreed}
                onChange={(e) => setTosAgreed(e.target.checked)}
                color={fieldColor}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                I agree to the{" "}
                <Link
                  href={tosUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color:
                      theme.palette.mode === "light"
                        ? theme.palette.secondary.main
                        : theme.palette.primary.main,
                    textDecoration: "underline",
                    "&:hover": {
                      color:
                        theme.palette.mode === "light"
                          ? theme.palette.secondary.dark
                          : theme.palette.primary.dark,
                    },
                  }}>
                  Terms of Service
                </Link>
              </Typography>
            }
            sx={{
              mt: 1,
              mb: 1,
              alignItems: "center",
            }}
          />
        </Box>
        <Button
          disabled={invalid}
          variant="contained"
          onClick={submitSignUp}
          processing={processing}>
          Sign Up!
        </Button>
        <ErrorText>{errorFeedback}</ErrorText>

        <TextLink theme={theme} onClick={() => navigate(ROUTES.SIGN_IN)}>
          Already have an account?
        </TextLink>
      </FormCard>
    </AuthLayout>
  );
};

const SignUpPage = (props) => {
  if (props.stillLoading) return null;
  return <SignUpComponent />;
};

export default SignUpPage;
